import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, 
  ShoppingCart, 
  Package, 
  Star,
  GamepadIcon,
  Globe,
  Monitor,
  Share2,
  Gift,
  Tv,
  PlayCircle,
  DollarSign,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Eye,
  X,
  ChevronDown,
  ChevronUp,
  Info,
  Grid,
  List,
  Tag
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import { TransactionService } from '../../services/transactionService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { useContext } from 'react';
import { AlertContext } from '../../contexts/AlertContext';

const buildImageStateKey = (scope, id, imageUrl = '') => `${scope}:${id}:${imageUrl}`;

const ProductCatalog = () => {
  const { currentUser, userProfile } = useAuth();
  const { addNotification } = useNotification();
  const { addAlert } = useContext(AlertContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [groupedGames, setGroupedGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState({});
  const [viewMode, setViewMode] = useState('categories'); // 'categories', 'games' or 'products'
  const [selectedGameData, setSelectedGameData] = useState(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileCategoryLayout] = useState('grid');
  const [mobileSortMode] = useState('count');
  const [catalogDisplayMode, setCatalogDisplayMode] = useState('grid');
  const [requestedGame, setRequestedGame] = useState(null);
  const [requestedProductId, setRequestedProductId] = useState(null);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  
  // Nouveaux états pour les fonctionnalités améliorées
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (viewMode === 'games') {
      groupProductsByGame();
    }
  }, [products, viewMode]);

  // Gérer les paramètres d'URL pour les catégories
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const gameParam = urlParams.get('game');
    const productIdParam = urlParams.get('productId');

    if (gameParam) {
      setRequestedGame(gameParam);
      setViewMode('games');
    } else {
      setRequestedGame(null);
    }

    if (productIdParam) {
      setRequestedProductId(productIdParam);
    } else {
      setRequestedProductId(null);
    }

    if (categoryParam && !gameParam && !productIdParam) {
      setSelectedCategory(categoryParam);
      setViewMode('games');
    }
  }, [location.search]);

  useEffect(() => {
    if (!requestedGame || groupedGames.length === 0) {
      return;
    }

    const normalizedRequestedGame = requestedGame.trim().toLowerCase();
    const matchedGame = groupedGames.find((game) => (game.name || '').trim().toLowerCase() === normalizedRequestedGame);

    if (matchedGame) {
      setSelectedGameData(matchedGame);
      setViewMode('products');
    }
  }, [requestedGame, groupedGames]);

  useEffect(() => {
    if (!requestedProductId || groupedGames.length === 0) {
      return;
    }

    const matchedGame = groupedGames.find((game) => (
      Array.isArray(game.products) && game.products.some((product) => product.id === requestedProductId)
    ));

    if (!matchedGame) {
      return;
    }

    const matchedProduct = matchedGame.products.find((product) => product.id === requestedProductId);
    setSelectedCategory(matchedProduct?.category || null);
    setSelectedGameData(matchedGame);
    setViewMode('products');
  }, [requestedProductId, groupedGames]);

  useEffect(() => {
    if (viewMode !== 'products' || !selectedGameData?.products?.length) {
      return;
    }

    setSelectedOfferIds((current) => {
      const nextSelections = { ...current };

      selectedGameData.products.forEach((product) => {
        const offers = product.offers || [];
        if (offers.length === 0) {
          return;
        }

        const selectedOfferId = nextSelections[product.id];
        const selectionStillExists = offers.some((offer) => offer.id === selectedOfferId);

        if (!selectionStillExists) {
          nextSelections[product.id] = offers[0].id;
        }
      });

      return nextSelections;
    });
  }, [viewMode, selectedGameData]);

  // Filtrer les jeux par catégorie
  useEffect(() => {
    if (selectedCategory && groupedGames.length > 0) {
      const filtered = groupedGames.filter(game => {
        return game.products.some(product => 
          product.category && getCategoryKey(product.category) === getCategoryKey(selectedCategory)
        );
      });
      setFilteredGames(filtered);
    } else {
      setFilteredGames(groupedGames);
    }
  }, [selectedCategory, groupedGames]);

  const normalizeCategory = (category) => {
    if (!category) return 'Autres';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  const getCategoryKey = (category) => {
    return CategoryService.normalizeCategory((category || '').trim());
  };

  // Fonction pour dériver les catégories à partir des produits
  const deriveCategories = (products) => {
    console.log('🔍 Dérivation des catégories pour', products.length, 'produits');
    const categoryMap = new Map();
    
    products.forEach(product => {
      console.log('🔍 Produit:', product.name, 'Status:', product.status, 'Catégorie:', product.category);
      // Les produits sont déjà filtrés par status === 'active' dans getActiveProducts()
      const categoryName = normalizeCategory(product.category);
      const categoryId = getCategoryKey(categoryName);
      console.log('🏷️ Catégorie normalisée:', categoryName, 'ID:', categoryId);
      
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          id: categoryId,
          name: categoryName,
          productCount: 0,
          games: new Set(),
          imageUrl: null,
          products: []
        });
      }
      
      const category = categoryMap.get(categoryId);
      category.productCount++;
      category.products.push(product);
      
      // Ajouter le jeu à la liste des jeux de cette catégorie
      if (product.game) {
        category.games.add(product.game);
      }
    });
    
    const result = Array.from(categoryMap.values())
      .map(category => ({
        ...category,
        gameCount: category.games.size
      }))
      .sort((a, b) => b.productCount - a.productCount);
    
    console.log('✅ Catégories finales:', result);
    return result;
  };

  const mergeCategoriesWithMetadata = (derivedCategories, categoryDocuments) => {
    const categoryDocsMap = new Map(
      (categoryDocuments || []).map((categoryDoc) => [
        getCategoryKey(categoryDoc.name || categoryDoc.id),
        categoryDoc
      ])
    );

    return derivedCategories.map((category) => {
      const storedCategory = categoryDocsMap.get(getCategoryKey(category.name));

      return {
        ...category,
        id: storedCategory?.id || category.id,
        name: storedCategory?.name || category.name,
        imageUrl: storedCategory?.imageUrl || category.imageUrl || null,
        status: storedCategory?.status || 'active'
      };
    });
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      console.log('🔄 Chargement des produits et catégories...');
      
      const [activeProducts, activeCategories] = await Promise.all([
        ProductService.getActiveProducts(),
        CategoryService.getActiveCategories()
      ]);

      console.log('📦 Produits chargés:', activeProducts.length, activeProducts);
      const activeCardCategories = activeCategories.filter((category) => (
        CategoryService.matchesSection(category, CategoryService.CATEGORY_SECTIONS.CARDS)
      ));
      const allowedCategoryKeys = new Set(
        activeCardCategories.map((category) => getCategoryKey(category.name || category.id))
      );
      const resolvedCardProducts = ProductService.filterProductsBySection(
        activeProducts,
        activeCategories,
        CategoryService.CATEGORY_SECTIONS.CARDS
      );
      const filteredProducts = allowedCategoryKeys.size > 0
        ? resolvedCardProducts.filter((product) => allowedCategoryKeys.has(getCategoryKey(product.category)))
        : resolvedCardProducts;

      setProducts(filteredProducts);
      
      // On conserve les vraies images/categories définies par l'admin, puis on y fusionne
      // les compteurs et les produits dérivés à partir des produits actifs.
      const categoriesWithProducts = mergeCategoriesWithMetadata(
        deriveCategories(filteredProducts),
        activeCardCategories
      );
      setCategories(categoriesWithProducts);
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
      addAlert('error', 'Erreur lors du chargement', 'Impossible de charger les produits et catégories', 'high');
    } finally {
      setLoading(false);
    }
  };

  const groupProductsByGame = () => {
    const gameGroups = {};
    
    products.forEach(product => {
      if (!gameGroups[product.game]) {
        gameGroups[product.game] = {
          name: product.game,
          products: [],
          activeProducts: 0,
          totalStock: 0,
          minPrice: Infinity
        };
      }
      
      gameGroups[product.game].products.push(product);
      
      if (product.status === 'active') {
        gameGroups[product.game].activeProducts++;
      }
      
      // Calculer le stock total et le prix minimum
      product.offers?.forEach(offer => {
        gameGroups[product.game].totalStock += offer.stock || 0;
        if (offer.price < gameGroups[product.game].minPrice) {
          gameGroups[product.game].minPrice = offer.price;
        }
      });
    });

    const groupedGamesArray = Object.values(gameGroups);
    setGroupedGames(groupedGamesArray);
  };

  const handleGameClick = (game) => {
    setSelectedGameData(game);
    setViewMode('products');
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category.name);
    // Naviguer vers la vue des jeux pour cette catégorie
    setViewMode('games');
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setViewMode('categories');
  };

  const handleBackToGames = () => {
    setSelectedGameData(null);
    setViewMode('games');
  };

  const canGoBackInCatalog = viewMode === 'games' || viewMode === 'products';

  const handleUniversalBack = () => {
    if (viewMode === 'products') {
      handleBackToGames();
      return;
    }

    if (viewMode === 'games') {
      handleBackToCategories();
    }
  };

  // Fonction pour basculer l'affichage de la description
  const toggleDescription = (productId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handlePurchase = async (productId, offerId, quantity = 1) => {
    if (!userProfile || userProfile.status !== 'active') {
      addNotification('error', 'Votre compte doit être activé pour effectuer des achats');
      return;
    }

    // Trouver le produit et l'offre pour vérifier le prix
    const product = products.find(p => p.id === productId);
    const offer = product?.offers?.find(o => o.id === offerId);
    
    if (!product || !offer) {
      addNotification('error', 'Produit ou offre introuvable');
      return;
    }

    // Vérifier le solde avant de procéder à l'achat
    const currentBalance = userProfile?.balance || 0;
    const totalCost = offer.price * quantity;
    
    if (currentBalance < totalCost) {
      addNotification('error', `Solde insuffisant ! Vous avez ${currentBalance.toLocaleString('fr-DZ')} DA mais il faut ${totalCost.toLocaleString('fr-DZ')} DA pour cet achat.`);
      return;
    }

    const loadingKey = `${productId}-${offerId}`;
    setPurchaseLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      await TransactionService.purchaseCodes(
        currentUser.uid,
        productId,
        offerId,
        quantity
      );

      addNotification('success', 'Achat effectué avec succès !');
      navigate('/card-history', {
        state: {
          fromPurchase: true,
          productId,
          offerId
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'achat:', error);
      addNotification('error', error.message || 'Erreur lors de l\'achat');
    } finally {
      setPurchaseLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    return `${formatted} DA`;
  };

  const buildCategoryMeta = (category) => {
    return `${category.productCount} produit${category.productCount > 1 ? 's' : ''}`;
  };

  const buildGameMeta = (game) => {
    return `${game.totalStock} codes • ${formatCurrency(game.minPrice)}`;
  };

  const getCardOfferLabel = (offer) => {
    const value = String(offer?.value || '').trim();
    const name = String(offer?.name || '').trim();

    if (value && value.toLowerCase() !== name.toLowerCase()) {
      return value;
    }

    return value || name || 'Offre';
  };

  const getCardOfferDisplay = (offer) => {
    const fullLabel = getCardOfferLabel(offer);
    const match = fullLabel.match(/^(.*?)(?:\s+)([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,24})$/u);

    if (match && !/\d/.test(match[2])) {
      return {
        primary: String(match[1] || '').trim() || fullLabel,
        unit: String(match[2] || '').trim(),
        fullLabel
      };
    }

    return {
      primary: fullLabel,
      unit: '',
      fullLabel
    };
  };

  const resolveCardCoverImage = (product) => {
    return String(product?.coverImageUrl || product?.imageUrl || product?.detailImageUrl || '').trim();
  };

  const resolveCardProductImage = (product) => {
    return String(product?.detailImageUrl || product?.imageUrl || product?.coverImageUrl || '').trim();
  };

  const resolveCardUnitIcon = (product) => {
    return String(product?.unitIconUrl || '').trim();
  };

  const getCategoryPresentation = (categoryName) => {
    const normalized = (categoryName || '').toLowerCase();

    if (normalized.includes('tv') || normalized.includes('music') || normalized.includes('musique')) {
      return {
        icon: Tv,
        accentClass: 'text-amber-500 bg-amber-50',
        badgeClass: 'text-amber-400',
        actionClass: 'text-amber-500',
        cardClass: 'border-amber-100 bg-gradient-to-br from-white via-amber-50/70 to-orange-50/90',
        imageShellClass: 'from-amber-50 via-white to-orange-50',
        orbClass: 'bg-amber-200/60',
        description: 'Cartes et credits pour TV, musique et divertissement'
      };
    }

    if (normalized.includes('gift') || normalized.includes('cadeau')) {
      return {
        icon: Gift,
        accentClass: 'text-rose-500 bg-rose-50',
        badgeClass: 'text-rose-400',
        actionClass: 'text-rose-500',
        cardClass: 'border-rose-100 bg-gradient-to-br from-white via-rose-50/70 to-pink-50/90',
        imageShellClass: 'from-rose-50 via-white to-pink-50',
        orbClass: 'bg-rose-200/60',
        description: 'Cartes cadeaux et bons d achat'
      };
    }

    if (normalized.includes('stream')) {
      return {
        icon: PlayCircle,
        accentClass: 'text-cyan-500 bg-cyan-50',
        badgeClass: 'text-cyan-400',
        actionClass: 'text-cyan-500',
        cardClass: 'border-cyan-100 bg-gradient-to-br from-white via-cyan-50/70 to-sky-50/90',
        imageShellClass: 'from-cyan-50 via-white to-sky-50',
        orbClass: 'bg-cyan-200/60',
        description: 'Abonnements et credits streaming'
      };
    }

    if (normalized.includes('pc')) {
      return {
        icon: Monitor,
        accentClass: 'text-violet-500 bg-violet-50',
        badgeClass: 'text-violet-400',
        actionClass: 'text-violet-500',
        cardClass: 'border-violet-100 bg-gradient-to-br from-white via-violet-50/70 to-fuchsia-50/90',
        imageShellClass: 'from-violet-50 via-white to-fuchsia-50',
        orbClass: 'bg-violet-200/60',
        description: 'Cartes et credits pour vos jeux PC'
      };
    }

    if (
      normalized.includes('social') ||
      normalized.includes('reseau') ||
      normalized.includes('réseau')
    ) {
      return {
        icon: Share2,
        accentClass: 'text-emerald-500 bg-emerald-50',
        badgeClass: 'text-emerald-400',
        actionClass: 'text-emerald-500',
        cardClass: 'border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-green-50/90',
        imageShellClass: 'from-emerald-50 via-white to-green-50',
        orbClass: 'bg-emerald-200/60',
        description: 'Cartes et credits pour vos reseaux sociaux'
      };
    }

    return {
      icon: GamepadIcon,
      accentClass: 'text-blue-500 bg-blue-50',
      badgeClass: 'text-blue-400',
      actionClass: 'text-blue-500',
      cardClass: 'border-blue-100 bg-gradient-to-br from-white via-blue-50/70 to-sky-50/90',
      imageShellClass: 'from-blue-50 via-white to-sky-50',
      orbClass: 'bg-blue-200/60',
      description: 'Cartes et credits pour vos jeux mobile preferes'
    };
  };

  const displayedCategories = categories
    .filter((category) => {
      const query = searchTerm.trim().toLowerCase();
      if (!query) return true;

      return (
        category.name?.toLowerCase().includes(query) ||
        category.products?.some((product) => product.game?.toLowerCase().includes(query))
      );
    })
    .sort((first, second) => {
      if (mobileSortMode === 'name') {
        return first.name.localeCompare(second.name, 'fr');
      }

      return second.productCount - first.productCount;
    });

  const getUniqueGames = () => {
    return [...new Set(products.map(p => p.game))];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
        {viewMode !== 'products' ? (
          <div className="mb-6 hidden flex-col gap-4 rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:flex md:p-6">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
              <div className="flex flex-1 items-center gap-3">
                {viewMode === 'games' ? (
                  <button
                    type="button"
                    onClick={handleUniversalBack}
                    className="inline-flex h-12 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour aux categories
                  </button>
                ) : null}
              
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={viewMode === 'games' ? 'Rechercher un produit...' : 'Rechercher un produit...'}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </div>
              </div>

              <div className="inline-flex h-12 items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setCatalogDisplayMode('grid')}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition ${
                    catalogDisplayMode === 'grid'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  aria-label="Afficher en grille"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setCatalogDisplayMode('list')}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition ${
                    catalogDisplayMode === 'list'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                  aria-label="Afficher en liste"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ) : null}
        
        {/* Categories Grid */}
        {viewMode === 'categories' ? (
          <>
            <div className="mb-5 space-y-4 md:hidden">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Rechercher un produit..."
                  className="h-12 w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 text-sm text-gray-700 shadow-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            {categories.length === 0 ? (
              <div className="text-center py-12">
                <Grid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune catégorie disponible
                </h3>
                <p className="text-gray-500 mb-4">
                  Les catégories apparaîtront ici une fois que des produits seront ajoutés.
                </p>
              </div>
            ) : (
              <>
                <div className={`grid gap-4 md:hidden ${mobileCategoryLayout === 'grid' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {displayedCategories.map((category) => {
                    const presentation = getCategoryPresentation(category.name);
                    const AccentIcon = presentation.icon;
                    const mobileCategoryImageKey = buildImageStateKey('cards-mobile-category', category.id, category.imageUrl || '');

                    if (mobileCategoryLayout === 'grid') {
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryClick(category)}
                          className={`group relative min-h-[198px] overflow-hidden rounded-[26px] border p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.08)] transition active:scale-[0.99] ${presentation.cardClass}`}
                        >
                          <div className={`absolute -right-6 top-10 h-20 w-20 rounded-full blur-2xl ${presentation.orbClass}`} />
                          <div className={`absolute -bottom-6 -left-3 h-16 w-16 rounded-full blur-2xl ${presentation.orbClass}`} />

                          <div className="relative z-10 flex items-start gap-3">
                            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                              <AccentIcon className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 flex-1">
                              <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-5 text-slate-800">
                                {category.name}
                              </h3>
                            </div>
                          </div>

                          <div className={`relative z-10 mt-3 h-[122px] overflow-hidden rounded-[22px] bg-gradient-to-br ${presentation.imageShellClass}`}>
                            {category.imageUrl && !imageLoadErrors[mobileCategoryImageKey] ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [mobileCategoryImageKey]: true }))}
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <Tag className="h-10 w-10 text-slate-300" />
                              </div>
                            )}

                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white/60 to-transparent" />
                          </div>

                          <div className={`absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur ${presentation.actionClass}`}>
                            <ArrowRight className="h-4 w-4" />
                          </div>
                        </button>
                      );
                    }

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleCategoryClick(category)}
                        className="overflow-hidden rounded-3xl border border-gray-200 bg-white text-left shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex min-h-[170px]">
                          <div className="flex w-[40%] min-w-0 flex-col justify-between p-4">
                            <div>
                              <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                                <AccentIcon className="h-5 w-5" />
                              </div>

                              <h3 className="text-xl font-semibold leading-tight text-gray-900">{category.name}</h3>
                              <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${presentation.badgeClass}`}>
                                {category.productCount} produit{category.productCount > 1 ? 's' : ''}
                              </span>
                              <p className="mt-4 text-sm leading-6 text-gray-500">{presentation.description}</p>
                            </div>
                          </div>

                          <div className="w-[60%] min-w-0 overflow-hidden">
                            {category.imageUrl && !imageLoadErrors[mobileCategoryImageKey] ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="h-full w-full object-cover"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [mobileCategoryImageKey]: true }))}
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
                                <Tag className="h-12 w-12 text-white/70" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                          <span className={`text-sm font-semibold ${presentation.actionClass}`}>
                            Voir les produits
                          </span>
                          <ArrowLeft className={`h-4 w-4 rotate-180 ${presentation.actionClass}`} />
                        </div>
                      </button>
                    );
                  })}
                </div>

                {displayedCategories.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center md:hidden">
                    <Search className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-4 text-sm text-gray-500">Aucune categorie ne correspond a votre recherche.</p>
                  </div>
                ) : null}

                {displayedCategories.length === 0 ? (
                  <div className="hidden rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center md:block">
                    <p className="text-base font-semibold text-slate-700">Aucune categorie trouvee</p>
                    <p className="mt-2 text-sm text-slate-500">Essayez un autre mot-cle pour retrouver une categorie.</p>
                  </div>
                ) : catalogDisplayMode === 'grid' ? (
                  <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
                    {displayedCategories.map((category, index) => {
                      const presentation = getCategoryPresentation(category.name);
                      const AccentIcon = presentation.icon;
                      const categoryGridImageKey = buildImageStateKey('cards-category-grid', category.id, category.imageUrl || '');

                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryClick(category)}
                          className="group flex h-full flex-col rounded-[26px] border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
                        >
                          {category.imageUrl && !imageLoadErrors[categoryGridImageKey] ? (
                            <div className="relative h-36 overflow-hidden rounded-2xl">
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="h-full w-full object-cover"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [categoryGridImageKey]: true }))}
                              />
                              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/25 to-transparent" />
                            </div>
                          ) : (
                            <div className={`relative flex h-36 items-end overflow-hidden rounded-2xl bg-gradient-to-br ${presentation.cardClass.includes('bg-gradient-to-br') ? presentation.cardClass.split('bg-gradient-to-br ')[1] : 'from-sky-600 via-blue-600 to-indigo-700'} p-4 text-white`}>
                              <div className="absolute left-3 top-3 rounded-full bg-white/15 p-2 backdrop-blur">
                                <AccentIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-2xl font-black tracking-tight">{category.name}</p>
                              </div>
                            </div>
                          )}

                          <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-base font-semibold text-slate-900">{category.name}</h3>
                                <p className="mt-1 text-sm text-slate-500">{buildCategoryMeta(category)}</p>
                              </div>
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:text-blue-600">
                                <ArrowRight className="h-4 w-4" />
                              </span>
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                              <span>{category.gameCount || 0} jeu(x)</span>
                              <span>Voir les produits</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="hidden space-y-3 md:block">
                    {displayedCategories.map((category, index) => {
                      const presentation = getCategoryPresentation(category.name);
                      const AccentIcon = presentation.icon;
                      const categoryListImageKey = buildImageStateKey('cards-category-list', category.id, category.imageUrl || '');

                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryClick(category)}
                          className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                        >
                          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                            {category.imageUrl && !imageLoadErrors[categoryListImageKey] ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                className="h-full w-full object-cover"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [categoryListImageKey]: true }))}
                              />
                            ) : (
                              <div className={`flex h-full w-full items-center justify-center ${presentation.accentClass}`}>
                                <AccentIcon className="h-5 w-5" />
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-semibold text-slate-900">{category.name}</h3>
                            <p className="mt-1 truncate text-sm text-slate-500">{buildCategoryMeta(category)}</p>
                          </div>

                          <span className="text-xs text-slate-400">{category.gameCount || 0} jeu(x)</span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-blue-600" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
        
        {/* Games Grid */}
        {viewMode === 'games' ? (
          <>
            <div className="mb-5 space-y-4 md:hidden">
              <div className="flex items-center gap-3">
                {canGoBackInCatalog ? (
                  <button
                    onClick={handleUniversalBack}
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-gray-300 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                    aria-label="Retour"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                ) : null}

                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Rechercher un produit..."
                    className="h-12 w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 text-sm text-gray-700 shadow-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>
            </div>

            {filteredGames.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun jeu trouvé
                </h3>
                <p className="text-gray-500 mb-4">
                  {selectedCategory 
                    ? `Aucun jeu trouvé dans la catégorie "${selectedCategory}".`
                    : 'Aucun jeu disponible pour le moment.'
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 md:hidden">
                  {filteredGames.map((game, index) => {
                    const presentation = getCategoryPresentation(
                      game.products?.[0]?.category || selectedCategory || game.name
                    );
                    const AccentIcon = presentation.icon;
                    const mobileGameImageUrl = resolveCardCoverImage(game.products?.[0]);
                    const mobileGameImageKey = buildImageStateKey('cards-mobile-game', game.name, mobileGameImageUrl);

                    return (
                      <button
                        key={`${game.name}-${index}`}
                        type="button"
                        onClick={() => handleGameClick(game)}
                        className={`group relative min-h-[190px] overflow-hidden rounded-[26px] border p-3 text-left shadow-[0_12px_30px_rgba(15,23,42,0.08)] transition active:scale-[0.99] ${presentation.cardClass}`}
                      >
                        <div className={`absolute -right-6 top-10 h-20 w-20 rounded-full blur-2xl ${presentation.orbClass}`} />
                        <div className={`absolute -bottom-6 -left-3 h-16 w-16 rounded-full blur-2xl ${presentation.orbClass}`} />

                        <div className="relative z-10 flex items-start gap-3">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${presentation.accentClass}`}>
                            <AccentIcon className="h-5 w-5" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="line-clamp-2 text-[1.05rem] font-semibold leading-5 text-slate-800">
                              {game.name}
                            </h3>
                          </div>
                        </div>

                        <div className={`relative z-10 mt-4 h-[108px] overflow-hidden rounded-[22px] bg-gradient-to-br ${presentation.imageShellClass}`}>
                          {mobileGameImageUrl && !imageLoadErrors[mobileGameImageKey] ? (
                            <img
                              src={mobileGameImageUrl}
                              alt={game.name}
                              className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                              onError={() => setImageLoadErrors((current) => ({ ...current, [mobileGameImageKey]: true }))}
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <Package className="h-10 w-10 text-slate-300" />
                            </div>
                          )}

                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white/60 to-transparent" />
                        </div>

                        <div className="relative z-10 mt-3 flex items-center justify-between">
                          <span className="text-[11px] font-medium text-slate-500">
                            {game.totalStock} codes • {formatCurrency(game.minPrice)}
                          </span>
                        </div>

                        <div className={`absolute bottom-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur ${presentation.actionClass}`}>
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </button>
                    );
                  })}
                </div>

                {catalogDisplayMode === 'grid' ? (
                  <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
                    {filteredGames.map((game, index) => {
                      const gameImageUrl = resolveCardCoverImage(game.products?.[0]);
                      const gameImageKey = buildImageStateKey('cards-game-grid', game.name, gameImageUrl);

                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleGameClick(game)}
                          className="group flex h-full flex-col rounded-[26px] border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
                        >
                          {gameImageUrl && !imageLoadErrors[gameImageKey] ? (
                            <div className="relative h-36 overflow-hidden rounded-2xl">
                              <img
                                src={gameImageUrl}
                                alt={game.name}
                                className="h-full w-full object-cover"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [gameImageKey]: true }))}
                              />
                              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/25 to-transparent" />
                            </div>
                          ) : (
                            <div className="relative flex h-36 items-end overflow-hidden rounded-2xl bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-700 p-4 text-white">
                              <p className="text-2xl font-black tracking-tight">{game.name}</p>
                            </div>
                          )}

                          <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-base font-semibold text-slate-900">{game.name}</h3>
                                <p className="mt-1 text-sm text-slate-500">{buildGameMeta(game)}</p>
                              </div>
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:text-blue-600">
                                <ArrowRight className="h-4 w-4" />
                              </span>
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                              <span>{game.products.length} produit(s)</span>
                              <span>Voir les offres</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="hidden space-y-3 md:block">
                    {filteredGames.map((game, index) => {
                      const presentation = getCategoryPresentation(
                        game.products?.[0]?.category || selectedCategory || game.name
                      );
                      const AccentIcon = presentation.icon;
                      const gameListImageUrl = resolveCardCoverImage(game.products?.[0]);
                      const gameListImageKey = buildImageStateKey('cards-game-list', game.name, gameListImageUrl);

                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleGameClick(game)}
                          className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:shadow-md"
                        >
                          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                            {gameListImageUrl && !imageLoadErrors[gameListImageKey] ? (
                              <img
                                src={gameListImageUrl}
                                alt={game.name}
                                className="h-full w-full object-cover"
                                onError={() => setImageLoadErrors((current) => ({ ...current, [gameListImageKey]: true }))}
                              />
                            ) : (
                              <div className={`flex h-full w-full items-center justify-center ${presentation.accentClass}`}>
                                <AccentIcon className="h-5 w-5" />
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-semibold text-slate-900">{game.name}</h3>
                            <p className="mt-1 truncate text-sm text-slate-500">{buildGameMeta(game)}</p>
                          </div>

                          <span className="text-xs text-slate-400">{game.products.length} produit(s)</span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-blue-600" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="space-y-6">
          <div className="space-y-6">
            {selectedGameData?.products?.map((product) => {
              const productImageUrl = resolveCardProductImage(product);
              const productImageKey = buildImageStateKey('cards-product-detail', product.id, productImageUrl);
              const unitIconUrl = resolveCardUnitIcon(product);
              const unitIconKey = buildImageStateKey('cards-unit-icon', product.id, unitIconUrl);

              return (
              <div
                key={product.id}
                className="space-y-6"
              >
                <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                  <button
                    type="button"
                    onClick={handleUniversalBack}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                  </button>

                  <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-start">
                    <div className="flex gap-4">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-[24px] bg-slate-100 sm:h-28 sm:w-28">
                        {productImageUrl && !imageLoadErrors[productImageKey] ? (
                          <img
                            src={productImageUrl}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            onError={() => setImageLoadErrors((current) => ({ ...current, [productImageKey]: true }))}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-xl font-bold text-white">
                            {(product.game || product.name || 'C').charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0">
                        <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                          {product.game || product.name}
                        </h2>
                        <div className="mt-3 inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                          {product.category || 'Cartes'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                  <div className="flex flex-col gap-3">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 sm:text-2xl">Choisissez votre variante</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Selectionnez la carte qui correspond a l achat.
                      </p>
                    </div>
                    {selectedOfferIds[product.id] ? (
                      <div className="inline-flex w-fit items-center self-start rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        Offre choisie: {getCardOfferLabel(product.offers?.find((offer) => offer.id === selectedOfferIds[product.id]))}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {(product.offers || []).map((offer) => {
                      const isSelected = selectedOfferIds[product.id] === offer.id;
                      const isOutOfStock = Number(offer.stock || 0) <= 0;
                      const offerDisplay = getCardOfferDisplay(offer);

                      return (
                        <button
                          key={offer.id}
                          type="button"
                          onClick={() => setSelectedOfferIds((current) => ({ ...current, [product.id]: offer.id }))}
                          className={`relative rounded-2xl border bg-white p-3 text-left transition-all duration-200 ${
                            isSelected
                              ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.10)]'
                              : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                          }`}
                        >
                          {isSelected ? (
                            <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white shadow-sm">
                              <CheckCircle className="h-3.5 w-3.5" />
                            </span>
                          ) : null}

                          <div className="flex min-h-[72px] items-start gap-2.5 pr-7">
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-[#f7f5ef]">
                              {unitIconUrl && !imageLoadErrors[unitIconKey] ? (
                                <img
                                  src={unitIconUrl}
                                  alt={product.name}
                                  className="h-6 w-6 object-contain"
                                  onError={() => setImageLoadErrors((current) => ({ ...current, [unitIconKey]: true }))}
                                />
                              ) : (
                                <Package className="h-4 w-4 text-slate-500" />
                              )}
                            </span>

                            <div className="min-w-0">
                              <p className="break-words text-sm font-black leading-[1.15] text-slate-900 sm:text-[15px]">
                                {offerDisplay.primary}
                              </p>
                              {offerDisplay.unit ? (
                                <p className="mt-1 break-words text-[11px] font-semibold leading-4 text-slate-500 sm:text-[13px]">
                                  {offerDisplay.unit}
                                </p>
                              ) : null}
                              <p className="mt-2 text-xs font-medium text-slate-500 sm:text-sm">
                                {formatCurrency(offer.price)}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            {offer.region ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                <Globe className="h-3 w-3" />
                                {offer.region}
                              </span>
                            ) : null}
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                isOutOfStock ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                              }`}
                            >
                              <Package className="h-3 w-3" />
                              {isOutOfStock ? 'Rupture' : `${offer.stock} en stock`}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const selectedOffer = product.offers?.find((offer) => offer.id === selectedOfferIds[product.id]);
                    const loadingKey = selectedOffer ? `${product.id}-${selectedOffer.id}` : '';
                    const isLoadingSelected = selectedOffer ? purchaseLoading[loadingKey] : false;
                    const isOutOfStock = !selectedOffer || Number(selectedOffer.stock || 0) <= 0;
                    const hasInsufficientBalance = selectedOffer && (userProfile?.balance || 0) < selectedOffer.price;

                    return (
                      <>
                        <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                          <div className="flex items-start gap-3">
                            <div className="rounded-xl bg-white p-2 text-blue-600 shadow-sm">
                              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-900 sm:text-base">Paiement 100% securise</h3>
                              <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                                Vos informations sont protegees et votre achat est traite instantanement.
                              </p>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => selectedOffer && handlePurchase(product.id, selectedOffer.id, 1)}
                          disabled={!selectedOffer || isLoadingSelected || isOutOfStock || hasInsufficientBalance}
                          className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 text-sm font-semibold text-white transition hover:from-blue-700 hover:to-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:py-3.5 sm:text-base"
                        >
                          {isLoadingSelected ? (
                            <LoadingSpinner size="sm" />
                          ) : isOutOfStock ? (
                            'Carte indisponible'
                          ) : (
                            `Acheter • ${formatCurrency(selectedOffer?.price || 0)}`
                          )}
                        </button>

                        {hasInsufficientBalance ? (
                          <p className="mt-3 text-center text-xs font-medium text-red-500">
                            Solde insuffisant pour cette offre.
                          </p>
                        ) : null}
                      </>
                    );
                  })()}
                </div>

                {product.description ? (
                  <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                    <h3 className="text-xl font-bold text-slate-900">Description du produit</h3>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-600 sm:text-base">
                      {product.description}
                    </p>
                  </div>
                ) : null}
              </div>
              );
            })}
          </div>
          </div>
        )}

        {/* Empty States */}
        {viewMode === 'games' && groupedGames.length === 0 && (
          <div className="text-center py-12">
            <GamepadIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun jeu trouvé</h3>
            <p className="text-gray-500">
              Aucun jeu disponible pour le moment.
            </p>
          </div>
        )}

        {viewMode === 'products' && selectedGameData?.products?.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
                <p className="text-gray-500">
                  Aucun produit disponible pour {selectedGameData?.name}.
                </p>
              </div>
            )}
      </div>
    </div>
  );
};

export default ProductCatalog;
